import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { Spinner } from './components/ui'

const Layout = lazy(() => import('./components/Layout').then((module) => ({ default: module.Layout })))
const Login = lazy(() => import('./pages/Auth').then((module) => ({ default: module.Login })))
const Register = lazy(() => import('./pages/Auth').then((module) => ({ default: module.Register })))
const ForgotPassword = lazy(() => import('./pages/Auth').then((module) => ({ default: module.ForgotPassword })))
const ResetPassword = lazy(() => import('./pages/Auth').then((module) => ({ default: module.ResetPassword })))
const Overview = lazy(() => import('./pages/Overview').then((module) => ({ default: module.Overview })))
const FeedbackList = lazy(() => import('./pages/FeedbackList').then((module) => ({ default: module.FeedbackList })))
const Projects = lazy(() => import('./pages/Projects').then((module) => ({ default: module.Projects })))
const FormBuilder = lazy(() => import('./pages/FormBuilder').then((module) => ({ default: module.FormBuilder })))
const Settings = lazy(() => import('./pages/Settings').then((module) => ({ default: module.Settings })))
const Summaries = lazy(() => import('./pages/Summaries').then((module) => ({ default: module.Summaries })))
const Billing = lazy(() => import('./pages/Billing').then((module) => ({ default: module.Billing })))
const SDKSetup = lazy(() => import('./pages/SDKSetup').then((module) => ({ default: module.SDKSetup })))
const Landing = lazy(() => import('./pages/Landing').then((module) => ({ default: module.Landing })))
const Docs = lazy(() => import('./pages/Docs').then((module) => ({ default: module.Docs })))
const Admin = lazy(() => import('./pages/Admin').then((module) => ({ default: module.Admin })))
const Support = lazy(() => import('./pages/Support').then((module) => ({ default: module.Support })))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then((module) => ({ default: module.PrivacyPolicy })))
const TermsOfService = lazy(() => import('./pages/TermsOfService').then((module) => ({ default: module.TermsOfService })))
const ReactNativeFeedbackSdkPage = lazy(() => import('./pages/PlatformPages').then((module) => ({ default: module.ReactNativeFeedbackSdkPage })))
const ExpoFeedbackSdkPage = lazy(() => import('./pages/PlatformPages').then((module) => ({ default: module.ExpoFeedbackSdkPage })))
const SwiftFeedbackSdkPage = lazy(() => import('./pages/PlatformPages').then((module) => ({ default: module.SwiftFeedbackSdkPage })))

function FullscreenSpinner() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f7f9fb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Spinner />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></div>
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL
  if (loading) return <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></div>
  if (!user) return <Navigate to="/login" replace />
  if (adminEmail && user.email !== adminEmail) return <Navigate to="/dashboard/overview" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (user) return <Navigate to="/dashboard/overview" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<FullscreenSpinner />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/react-native-feedback-sdk" element={<ReactNativeFeedbackSdkPage />} />
            <Route path="/expo-feedback-sdk" element={<ExpoFeedbackSdkPage />} />
            <Route path="/swift-feedback-sdk" element={<SwiftFeedbackSdkPage />} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard/overview" replace />} />
              <Route path="overview" element={<Overview />} />
              <Route path="feedback" element={<FeedbackList />} />
              <Route path="projects" element={<Projects />} />
              <Route path="form-builder" element={<FormBuilder />} />
              <Route path="settings" element={<Settings />} />
              <Route path="billing" element={<Billing />} />
              <Route path="summaries" element={<Summaries />} />
              <Route path="setup" element={<SDKSetup />} />
              <Route path="support" element={<Support />} />
              <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  )
}
